import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useMessage } from '@/hooks/use-message';

interface AdminMessageComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GROUP_OPTIONS = [
  { id: '1', name: 'Main Padel Tennis Group', type: 'group' },
  { id: '2', name: 'Beginners', type: 'subgroup' },
  { id: '3', name: 'Intermediate', type: 'subgroup' },
  { id: '4', name: 'Advanced', type: 'subgroup' },
  { id: '5', name: 'Pros', type: 'subgroup' },
];

export function AdminMessageComposer({ open, onOpenChange }: AdminMessageComposerProps) {
  const { sendAdminMessage } = useMessage();
  const [message, setMessage] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedSubgroups, setSelectedSubgroups] = useState<string[]>([]);

  const handleGroupToggle = (groupId: string, type: 'group' | 'subgroup') => {
    if (type === 'group') {
      setSelectedGroups(prev => 
        prev.includes(groupId) 
          ? prev.filter(id => id !== groupId)
          : [...prev, groupId]
      );
    } else {
      setSelectedSubgroups(prev => 
        prev.includes(groupId) 
          ? prev.filter(id => id !== groupId)
          : [...prev, groupId]
      );
    }
  };

  const handleSend = () => {
    if (message.trim() && (selectedGroups.length > 0 || selectedSubgroups.length > 0)) {
      sendAdminMessage(selectedGroups, selectedSubgroups, message);
      setMessage('');
      setSelectedGroups([]);
      setSelectedSubgroups([]);
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setSelectedGroups([]);
    setSelectedSubgroups([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send Admin Message</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-3 block">Select Recipients:</Label>
            <div className="space-y-2">
              {GROUP_OPTIONS.map((option) => (
                <div 
                  key={option.id} 
                  className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={() => handleGroupToggle(option.id, option.type as 'group' | 'subgroup')}
                >
                  <Checkbox
                    id={option.id}
                    checked={
                      option.type === 'group' 
                        ? selectedGroups.includes(option.id)
                        : selectedSubgroups.includes(option.id)
                    }
                    onChange={() => {}} // Controlled by parent div
                    className="pointer-events-none" // Prevent checkbox from interfering with click
                  />
                  <Label htmlFor={option.id} className="text-sm cursor-pointer flex-1 pointer-events-none">
                    {option.name}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({option.type})
                    </span>
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="admin-message" className="text-sm font-medium mb-2 block">
              Message:
            </Label>
            <Textarea
              id="admin-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your admin message here..."
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend}
            disabled={!message.trim() || (selectedGroups.length === 0 && selectedSubgroups.length === 0)}
          >
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}